/**
 * BotController — Rivalen-KI.
 * Jagt Kunden/VIPs, liefert Schlepptau am eigenen Stand ab,
 * schießt Kunden aus fremdem Schlepptau frei, füllt nach, stiehlt.
 */
import type { Controller, ControlSelf, PlayerInput, WorldSense, Vec } from './PlayerControl';

type Mode = 'hunt' | 'refill' | 'steal' | 'attack' | 'wander' | 'coffee' | 'deliver' | 'drink';

const LOW_STAMINA = 25;

const TOW_DELIVER_COUNT = 2;
const TOW_DELIVER_VALUE = 80;

export class BotController implements Controller {
  private mode: Mode = 'hunt';
  private modeT = 0;
  private target: Vec | null = null;
  private fireCd = 0;
  private switchCd = 0;
  /** Für Debug/Smoke */
  get debugMode(): string {
    return this.mode;
  }

  getInput(world: WorldSense, self: ControlSelf, dt: number): PlayerInput {
    this.fireCd = Math.max(0, this.fireCd - dt);
    this.switchCd = Math.max(0, this.switchCd - dt);
    this.modeT -= dt;

    if (self.stunT > 0) {
      return { moveX: 0, moveY: 0, fire: false, ammoSwitch: false };
    }

    const lowAmmo = self.pensAmmo + self.flyersAmmo < 4 || self.pensAmmo === 0;
    const shouldDeliver =
      self.towCount >= TOW_DELIVER_COUNT || self.towValue >= TOW_DELIVER_VALUE;

    // Abwägung: volles Schlepptau (≥1,5× Schwelle) zuerst abliefern, sonst bei Müdigkeit Kaffee
    const urgentDeliver = self.towValue >= TOW_DELIVER_VALUE * 1.5;
    const tired = self.stamina < LOW_STAMINA && world.coffeeStands.length > 0;
    if (tired && !urgentDeliver) this.mode = 'drink';
    else if (shouldDeliver) this.mode = 'deliver';
    else if (self.hasCoffee) this.mode = 'coffee';
    else if (lowAmmo) this.mode = 'refill';
    else if (this.modeT <= 0) {
      const r = Math.random();
      if (r < 0.1) this.mode = 'coffee';
      else if (r < 0.2) this.mode = 'steal';
      else if (r < 0.28) this.mode = 'attack';
      else if (r < 0.35) this.mode = 'wander';
      else this.mode = 'hunt';
      this.modeT = 2.5 + Math.random() * 3.5;
    }

    let goal: Vec | null = null;
    let wantFlyer = false;
    let fire = false;
    let aimX: number | undefined;
    let aimY: number | undefined;

    if (this.mode === 'deliver') {
      goal = {
        x: world.ownStand.x + world.ownStand.w / 2,
        y: world.ownStand.y + world.ownStand.h + 28,
      };
      if (self.towCount === 0) {
        this.mode = 'hunt';
        this.modeT = 1.5;
      }
    } else if (this.mode === 'drink') {
      const pool = world.coffeeStands.filter((c) => c.drinkReady);
      const list = pool.length ? pool : world.coffeeStands;
      let best = list[0]!;
      let bestD = Infinity;
      for (const c of list) {
        const d = Math.hypot(c.x + c.w / 2 - self.x, c.y + c.h / 2 - self.y);
        if (d < bestD) {
          bestD = d;
          best = c;
        }
      }
      goal = { x: best.x + best.w / 2, y: best.y + best.h / 2 };
      if (self.stamina >= LOW_STAMINA) {
        this.mode = 'hunt';
        this.modeT = 1;
      }
    } else if (this.mode === 'coffee') {
      if (!self.hasCoffee) {
        const ready = world.coffeeStands.filter((c) => c.ready);
        const pool = ready.length ? ready : world.coffeeStands;
        if (pool.length) {
          let best = pool[0]!;
          let bestD = Infinity;
          for (const c of pool) {
            const d = Math.hypot(c.x + c.w / 2 - self.x, c.y + c.h / 2 - self.y);
            if (d < bestD) {
              bestD = d;
              best = c;
            }
          }
          goal = { x: best.x + best.w / 2, y: best.y + best.h / 2 };
        } else {
          this.mode = 'hunt';
        }
      } else {
        let bestC: { x: number; y: number } | null = null;
        let bestD = Infinity;
        for (const stand of world.coffeeStands) {
          const sx = stand.x + stand.w / 2;
          const sy = stand.y + stand.h / 2;
          for (const c of world.customers) {
            if (c.hp <= 0) continue;
            if (Math.hypot(c.x - sx, c.y - sy) > 120) continue;
            const d = Math.hypot(c.x - self.x, c.y - self.y);
            if (d < bestD) {
              bestD = d;
              bestC = c;
            }
          }
        }
        if (bestC) {
          goal = { x: bestC.x, y: bestC.y };
          aimX = bestC.x;
          aimY = bestC.y;
          if (bestD < 40 && this.fireCd <= 0) {
            fire = true;
            this.fireCd = 0.4;
          }
        } else {
          const stand = world.coffeeStands[0];
          if (stand) goal = { x: stand.x + stand.w / 2, y: stand.y + stand.h / 2 };
          else this.mode = 'hunt';
        }
      }
    } else if (this.mode === 'refill') {
      goal = {
        x: world.ownStand.x + world.ownStand.w / 2,
        y: world.ownStand.y + world.ownStand.h + 30,
      };
      if (self.pensAmmo >= 12 && self.flyersAmmo >= 2) {
        this.mode = 'hunt';
        this.modeT = 2;
      }
    } else if (this.mode === 'steal' && world.foreignStands.length) {
      const s = world.foreignStands[(Math.random() * world.foreignStands.length) | 0]!;
      goal = { x: s.x + s.w / 2, y: s.y + s.h + 24 };
    } else if (this.mode === 'attack' && world.rivals.length) {
      let best = world.rivals[0]!;
      let bestD = Infinity;
      for (const r of world.rivals) {
        if (r.id === self.id) continue;
        const d = Math.hypot(r.x - self.x, r.y - self.y);
        if (d < bestD) {
          bestD = d;
          best = r;
        }
      }
      goal = { x: best.x, y: best.y };
      aimX = best.x;
      aimY = best.y;
      if (bestD < 220 && self.pensAmmo > 0 && this.fireCd <= 0) {
        fire = true;
        this.fireCd = 0.35;
        wantFlyer = false;
      }
    } else if (this.mode === 'wander') {
      if (!this.target || Math.hypot(this.target.x - self.x, this.target.y - self.y) < 40) {
        this.target = {
          x: 200 + Math.random() * (world.worldW - 400),
          y: 200 + Math.random() * (world.worldH - 600),
        };
      }
      goal = this.target;
    } else {
      // hunt: freie Kunden/VIPs + fremdes Schlepptau
      let best: { x: number; y: number; r: number; flyer: boolean; freeTow?: boolean } | null =
        null;
      let bestD = Infinity;

      for (const t of world.enemyTows) {
        const d = Math.hypot(t.x - self.x, t.y - self.y);
        // leicht bevorzugen: freischießen
        const score = d * 0.85;
        if (score < bestD) {
          bestD = score;
          best = { x: t.x, y: t.y, r: t.r, flyer: false, freeTow: true };
        }
      }
      for (const v of world.vips) {
        if (v.hp <= 0) continue;
        const d = Math.hypot(v.x - self.x, v.y - self.y);
        if (d < bestD) {
          bestD = d;
          best = { x: v.x, y: v.y, r: v.r, flyer: true };
        }
      }
      for (const c of world.customers) {
        if (c.hp <= 0) continue;
        const d = Math.hypot(c.x - self.x, c.y - self.y);
        if (d < bestD) {
          bestD = d;
          best = {
            x: c.x,
            y: c.y,
            r: c.r,
            flyer: c.tier === 'gross' || c.tier === 'hart',
          };
        }
      }
      if (best) {
        goal = { x: best.x, y: best.y };
        aimX = best.x;
        aimY = best.y;
        wantFlyer = !best.freeTow && best.flyer && self.flyersAmmo > 0;
        const range = wantFlyer ? 180 : 260;
        if (bestD < range + best.r && this.fireCd <= 0) {
          const have = wantFlyer ? self.flyersAmmo > 0 : self.pensAmmo > 0;
          if (have) {
            fire = true;
            this.fireCd = 0.28;
          }
        }
      } else {
        goal = { x: world.worldW / 2, y: world.worldH * 0.4 };
      }
    }

    let ammoSwitch = false;
    const need = wantFlyer ? 'flyer' : 'pen';
    if (need !== self.activeAmmo && this.switchCd <= 0) {
      ammoSwitch = true;
      this.switchCd = 0.4;
    }

    let moveX = 0;
    let moveY = 0;
    if (goal) {
      const step = world.pathStep({ x: self.x, y: self.y }, goal) ?? goal;
      const dx = step.x - self.x;
      const dy = step.y - self.y;
      const len = Math.hypot(dx, dy) || 1;
      moveX = dx / len;
      moveY = dy / len;
    }

    for (const n of world.nobodys) {
      const d = Math.hypot(n.x - self.x, n.y - self.y);
      if (d < 50 && d > 1) {
        moveX += ((self.x - n.x) / d) * 0.35;
        moveY += ((self.y - n.y) / d) * 0.35;
      }
    }
    const ml = Math.hypot(moveX, moveY);
    if (ml > 1) {
      moveX /= ml;
      moveY /= ml;
    }

    return { moveX, moveY, aimX, aimY, fire, ammoSwitch };
  }
}
